
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { MortgageData } from "../CalculatorTabs";
import { AutoCalculateOptions } from "../utils/types";

// Import our sub-components
import HomePriceInput from "./subcomponents/HomePriceInput";
import LoanTypeSelector from "./subcomponents/LoanTypeSelector";
import DownPaymentInput from "./subcomponents/DownPaymentInput";
import InterestRateInput from "./subcomponents/InterestRateInput";
import LoanTermButtons from "./subcomponents/LoanTermButtons";
import ConditionalLoanFields from "./subcomponents/ConditionalLoanFields";
import CreditScoreSelector from "./subcomponents/CreditScoreSelector";

interface BasicInputsProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
  calculatorHandlers: {
    handleHomePriceChange: (value: number) => void;
    handleInterestRateChange: (value: number) => void;
    handleLoanTermChange: (value: number) => void;
    handleDownPaymentChange: (value: number) => void;
    handleLoanTypeChange: (value: any) => void;
    handleZipCodeChange: (value: string) => void;
    handleIsVeteranChange: (value: boolean) => void;
  };
}

const BasicInputs: React.FC<BasicInputsProps> = ({ 
  mortgageData, 
  setMortgageData, 
  autoCalculate, 
  calculatorHandlers 
}) => {
  const handleCreditScoreChange = (value: number) => {
    setMortgageData(prev => ({ ...prev, creditScore: value }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Property Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 1. Home Price - Primary input that drives all calculations */}
        <HomePriceInput 
          mortgageData={mortgageData}
          setMortgageData={setMortgageData}
          autoCalculate={autoCalculate}
          onHomePriceChange={calculatorHandlers.handleHomePriceChange}
        />
        
        {/* 2. Loan Type - Immediately follows home price as it affects all subsequent fields */}
        <LoanTypeSelector 
          mortgageData={mortgageData}
          setMortgageData={setMortgageData}
          autoCalculate={autoCalculate}
          onLoanTypeChange={calculatorHandlers.handleLoanTypeChange}
        />
        
        {/* 3. Credit Score - New addition for enhanced eligibility checking */}
        <CreditScoreSelector 
          mortgageData={mortgageData}
          setMortgageData={setMortgageData}
          autoCalculate={autoCalculate}
          onCreditScoreChange={handleCreditScoreChange}
        />
        
        {/* 4. Down Payment - Both dollar amount and percentage with live sync */}
        <DownPaymentInput 
          mortgageData={mortgageData}
          setMortgageData={setMortgageData}
          autoCalculate={autoCalculate}
          onDownPaymentChange={calculatorHandlers.handleDownPaymentChange}
        />
        
        {/* 5. Interest Rate - Affects monthly payment calculation */}
        <InterestRateInput 
          mortgageData={mortgageData}
          setMortgageData={setMortgageData}
          autoCalculate={autoCalculate}
          onInterestRateChange={calculatorHandlers.handleInterestRateChange}
        />
        
        {/* 6. Loan Term - Final parameter for payment calculation */}
        <LoanTermButtons 
          mortgageData={mortgageData}
          setMortgageData={setMortgageData}
          autoCalculate={autoCalculate}
          onLoanTermChange={calculatorHandlers.handleLoanTermChange}
        />
        
        {/* 7. Conditional Loan Fields - Additional fields based on loan type */}
        <ConditionalLoanFields 
          mortgageData={mortgageData}
          setMortgageData={setMortgageData}
          autoCalculate={autoCalculate}
        />
      </CardContent>
    </Card>
  );
};

export default BasicInputs;
